import React, { useLayoutEffect } from "react";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard/Dashboard";
import "./App.css";
import { Protected, ProtectedPage } from "./pages/Protected";
import {
  DashboardRoutes,
  DemoRoutes,
  GridcoRoutes,
  HppcRoutes,
  RajasthanRoutes,
  ChhattisgarhRoutes,
} from "./Routes";
import { Gnai } from "./pages/GNAi/Gnai";
import { useSelector } from "react-redux";
import { Documents } from "./pages/Documents/Documents";
import { Sidebar2 } from "./layout/Sidebar2";
import { Login2 } from "./pages/Login/Login2";
import MarketMonitoring from "./pages/MarketMonitoringCERC/MarketMonitoring";
import { ChartBuilder } from "./pages/ChartBuilder/ChartBuilder";
import { DamAnalysis } from "./pages/DamAnalysis/DamAnalysis";
import Dmrc from "./pages/Dmrc/Dmrc";
import RajDashboard from "./pages/Rajasthan/Raj_Dashboard";
import ChhattisgarhDashboard from "./pages/Chhattisgarh/CGS_Dashboard";
import DemoRajDashboard from "./pages/DemoState/Raj_Dashboard";
import GridcoDashboard from "./pages/Gridco/Gridco_Das";
import HpgclDashboard from "./pages/Hppc/Hppc_Das";
import DemoDashboard from "./pages/Demo/Raj_Dashboard";
import { RootState } from "./store/store";
import { MarketDerivativePage } from "./pages/market-derivative/MarketDerivativePage";
import DynamicRedirect from "./components/DynamicRedirect";
import Agent from "./pages/Dashboard/Agent/Agent";
import ExchangeAssistant from "./pages/PowerVidut/Index";

type RouteRenderOptions = {
  wrapWithProtected?: boolean;
};

const getContentClassName = (noDefaultPadding?: boolean) =>
  noDefaultPadding ? "content2" : "content";

const getProtectedPageProps = (pageId: string, accessKey?: string) =>
  accessKey ? { pageId, accessKey } : { pageId };

const renderProtectedRouteElement = (
  route: any,
  options?: RouteRenderOptions,
) => {
  if (route.notPrtoected) {
    return route.element;
  }

  const protectedContent = (
    <div className={getContentClassName(route.noDefaultPadding)}>
      <ProtectedPage
        {...getProtectedPageProps(`/${route.path}`, route.accessKey)}
      >
        {route.element}
      </ProtectedPage>
    </div>
  );

  return options?.wrapWithProtected ? (
    <Protected>{protectedContent}</Protected>
  ) : (
    protectedContent
  );
};

const renderRouteGroup = (routes: any[], options?: RouteRenderOptions) =>
  routes.map((route, index) => (
    <Route
      key={`${route.path}-${index}`}
      path={route.path}
      element={renderProtectedRouteElement(route, options)}
    />
  ));

const SidebarOutletLayout = () => (
  <>
    <Sidebar2 />
    <Outlet />
  </>
);

const ProtectedSidebarOutletLayout = () => (
  <Protected>
    <Sidebar2 />
    <Outlet />
  </Protected>
);

const protectedIndexContent = (
  element: React.ReactNode,
  pageId: string,
  accessKey?: string,
) => (
  <div className="content2">
    <ProtectedPage {...getProtectedPageProps(pageId, accessKey)}>
      {element}
    </ProtectedPage>
  </div>
);

function App() {
  const active = useSelector((state: RootState) => state.menu.isActive);
  const width = useSelector((state: any) => state.FilterWidthSlice.width);
  const location = useLocation();

  const isZeroMarginRoute =
    location.pathname.startsWith("/ruvnl") ||
    location.pathname.startsWith("/gridco") ||
    location.pathname.startsWith("/demo") ||
    location.pathname.startsWith(
      "/dashboard/consumptionAndGenerationAnalytics",
    ) ||
    location.pathname.startsWith("/dashboard/exchangeAnalysis");
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--gas-chart-margin",
      isZeroMarginRoute ? "0" : "1rem",
    );
  }, [isZeroMarginRoute]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--page-width",
      active ? "calc(100vw - 102px)" : "calc(100vw - 60px)",
    );
  }, [active]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty("--mainPage-width", width);
  }, [width]);

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <Protected>
              <DynamicRedirect />
            </Protected>
          }
        />
        <Route path="/login" element={<Login2 />} />

        <Route path="/dashboard" element={<SidebarOutletLayout />}>
          <Route
            index={true}
            element={
              <div className="content2">
                <Dashboard />
              </div>
            }
          />

          {renderRouteGroup(DashboardRoutes)}
        </Route>

        {/* <Route path="/trader" element={<CommonTrader />} /> */}

        {/* RUVNL Routes */}
        <Route path="/ruvnl" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(
              <RajDashboard />,
              "/ruvnl",
              "rajasthan",
            )}
          />
          {renderRouteGroup(RajasthanRoutes)}
        </Route>
        <Route path="/CSPDCL" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(
              <ChhattisgarhDashboard />,
              "/CSPDCL",
              "cg",
            )}
          />
          {renderRouteGroup(ChhattisgarhRoutes)}
        </Route>
        <Route path="/discom-demo" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(<DemoDashboard />, "/demoState")}
          />
          {renderRouteGroup(DemoRoutes)}
        </Route>

        {/* Demo routes */}

        <Route path="/demo" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(<DemoRajDashboard />, "/demoState")}
          />
          {renderRouteGroup(DemoRoutes)}
        </Route>

        {/* GRIDCO Routes */}
        <Route path="/gridco" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(<GridcoDashboard />, "/gridco")}
          />
          {renderRouteGroup(GridcoRoutes, { wrapWithProtected: true })}
        </Route>

        {/* HPGCL Routes */}
        <Route path="/hppc" element={<ProtectedSidebarOutletLayout />}>
          <Route
            index={true}
            element={protectedIndexContent(<HpgclDashboard />, "/hppc")}
          />

          {renderRouteGroup(HppcRoutes)}
        </Route>
        <Route
          path="/gnai"
          element={
            <Protected>
              <Sidebar2 />
              <div className="content">
                <ProtectedPage children={<Gnai />} pageId="/gnai" />
              </div>
            </Protected>
          }
        />
        <Route
          path="/agent"
          element={
            <Protected>
              <ProtectedPage children={<Agent />} pageId="/exchangeAnalysis" />
            </Protected>
          }
        />
        <Route
          path="/assistant"
          element={
            <Protected>
              <ExchangeAssistant />
            </Protected>
          }
        />
        <Route
          path="/derivative"
          element={
            <Protected>
              <div className="w-full h-full">
                <MarketDerivativePage />
              </div>
            </Protected>
          }
        />
        <Route
          path="/marketMonitoring"
          element={
            <Protected>
              <div className="">
                <ProtectedPage children={<MarketMonitoring />} pageId="/cerc" />
              </div>
            </Protected>
          }
        />
        <Route path="/chartBuilder" element={<ChartBuilder />} />
        <Route path="/damAnalysis" element={<DamAnalysis />} />
        <Route
          path="/dmrc"
          element={
            <Protected>
              <Sidebar2 />
              <div className="content2">
                <ProtectedPage children={<Dmrc />} pageId="/dmrc" />
              </div>
            </Protected>
          }
        />
        <Route
          path="/document"
          element={
            <Protected>
              <Sidebar2 />

              <div className="content2">
                <ProtectedPage pageId="/gnadoc" key={"gnadoc"}>
                  <Documents />
                </ProtectedPage>
              </div>
            </Protected>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
